import { OnboardPatientContext } from '@dfh-corp-sites/onboarding/context'
import { first, firstValueFrom, tap } from 'rxjs'
import { assign, createMachine, InterpreterFrom, StateConfig, StateFrom } from 'xstate'

import { persistedPatientState$, setPatientPersistedState } from '../hooks/useOnboardPatientPersist'

export type OnboardPatientEvent =
  | { type: 'RESTORE' }
  | { type: 'CONTINUE' }
  | { type: 'UPDATE_FIELDS'; data: Partial<OnboardPatientContext> }

export type OnboardPatientState = {
  value: 'initial' | 'createAccount' | 'createAccountPersist' | 'invalid' | 'complete'
  context: OnboardPatientContext
}

export type OnboardPatientMachine = typeof machine
export type OnboardPatientInterpreter = InterpreterFrom<typeof machine>
export type OnboardPatientObserverCallback = (state: StateFrom<typeof machine>) => void
export type OnboardPatientStateConfig = StateConfig<
  OnboardPatientContext & { customerId?: string },
  OnboardPatientEvent
>
export type OnboardPatientStateObject = StateFrom<typeof machine>
export type OnboardPatientStateValue = OnboardPatientState['value']

export interface OnboardPatientClaimParsed {
  state: OnboardPatientStateValue
  context: OnboardPatientContext
}

export const machine = createMachine<
  OnboardPatientContext & { customerId?: string },
  OnboardPatientEvent,
  OnboardPatientState
>({
  predictableActionArguments: true,
  id: 'onboard_customer',
  initial: 'initial',
  context: {
    hasPractitioner: undefined,
    firstName: '',
    lastName: '',
    phone: '',
  },
  states: {
    initial: {
      on: {
        '*': [
          {
            cond: ({ customerId }) => Boolean(customerId),
            target: 'complete',
          },
          {
            cond: ({ customerId }) => !customerId,
            target: 'createAccount',
          },
        ],
      },
    },
    createAccount: {
      on: {
        CONTINUE: {
          target: 'createAccountPersist',
        },
      },
    },
    createAccountPersist: {
      entry: 'clearPersistError',
      invoke: {
        src: (context) => (send) => {
          setPatientPersistedState({
            state: 'complete',
            context,
          })
          return firstValueFrom(
            persistedPatientState$.pipe(
              first((state) => state.loading === false),
              tap((state) => {
                if (state.result?._tag === 'Right') {
                  send({ type: 'CONTINUE' })
                } else {
                  send({ type: 'RESTORE' })
                }
              }),
            ),
          )
        },
        onError: {
          target: 'createAccount',
          actions: 'handlePersistError',
        },
      },
      on: {
        CONTINUE: {
          target: 'complete',
        },
        RESTORE: {
          target: 'createAccount',
          actions: 'handlePersistError',
        },
      },
    },
    invalid: {
      on: {
        RESTORE: 'complete',
      },
    },
    complete: {
      type: 'final',
      entry: 'submit',
    },
  },
  on: {
    UPDATE_FIELDS: {
      actions: assign((context, event) => ({
        ...context,
        ...(event.type === 'UPDATE_FIELDS' ? event.data : {}),
      })),
    },
  },
})
